import * as React from 'react';
import { useRecoilValue } from 'recoil';
import {
  IS_ALARM_LAYER,
  IS_ECO_DRIVE_LAYER,
  IS_EVENT_LAYER,
  IS_RIDE_LAYER,
  PLAYED_LINE_ALARM_LAYER_COLOR,
  PLAYED_LINE_ECODRIVE_LAYER_COLOR,
  PLAYED_LINE_EVENT_LAYER_COLOR,
  PLAYED_LINE_RIDE_LAYER_COLOR,
} from '../constants';
import { IPlayedLines, IRide, IRideLines, IRidePosition, TRideActiveLayer } from '../types';
import {
  addNewRideLinePositionForRide,
  createActiveRideLines,
  createRideLines,
} from '../RidePlayerService';
import { activeRideNumberAtom, selectedRidesAtom } from '../RidePlayerState';

interface IUseRideLine {
  rideLines: IRideLines;
  setRideLinesSize: (strokeWeight: number) => void;
  showAllRideLines: () => void;
  hideAllRideLines: () => void;
  playedRideLines: IPlayedLines;
  setRideLineOpacity: (rideNumber: number, opacity: number) => void;
  setRideLineColor: (rideNumber: number, color: string) => void;
  setAllRideLinesColor: (color: string, exceptRideNumber?: number) => void;
  hideAllPlayedRideLines: () => void;
  showAllPlayedRideLines: () => void;
  removePlayedLines: () => void;
  removeAllRideLines: () => void;
  setRideLineZIndex: (rideNumber: number, zIndex: number) => void;
  setPlayedRideLineZIndex: (rideNumber: number, zIndex: number) => void;
  setAllRideLinesZIndex: (zIndex: number, exceptRideNumber?: number) => void;
  setAllPlayedRideLinesZindex: (zIndex: number, exceptRideNumber?: number) => void;
  resetPlayedRideLine: (rideNumber: number) => void;
  resetAllPlayedRideLines: () => void;
  setPlayedRideLineColor: (color: string) => void;
}
/**
 * useRideLine custom hook
 * @param {google.maps.Map} map
 * @returns {IUseRideLine}
 */
const useRideLine = (
  map: google.maps.Map,
  rides: IRide[],
  activeLayer: TRideActiveLayer,
  actualRidePosition?: IRidePosition | null
): IUseRideLine => {
  const rideLines = React.useRef<IRideLines>({});
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);

  const getRideLineColorByLayer = () => {
    switch (activeLayer) {
      case IS_RIDE_LAYER: {
        return PLAYED_LINE_RIDE_LAYER_COLOR;
      }
      case IS_ECO_DRIVE_LAYER: {
        return PLAYED_LINE_ECODRIVE_LAYER_COLOR;
      }
      case IS_ALARM_LAYER: {
        return PLAYED_LINE_ALARM_LAYER_COLOR;
      }
      case IS_EVENT_LAYER: {
        return PLAYED_LINE_EVENT_LAYER_COLOR;
      }
      default: {
        return PLAYED_LINE_RIDE_LAYER_COLOR;
      }
    }
  };

  // Active played ride line
  const playedRideLines = React.useRef<IPlayedLines>(
    createActiveRideLines(map, rides, getRideLineColorByLayer())
  );

  /**
   * Set ride lines stroke weight (aka size)
   * @param {number} strokeWeight
   * @returns {void}
   */
  const setRideLinesSize = (strokeWeight = 2): void => {
    Object.keys(rideLines.current).forEach((rideNumber: string) => {
      const polyline = rideLines.current[rideNumber];
      if (polyline && polyline.strokeWeight !== strokeWeight) {
        polyline.setOptions({ strokeWeight });
      }
    });
  };

  const hideAllPlayedRideLines = () => {
    Object.keys(playedRideLines.current).forEach((rideNumber: string) => {
      const polyline = playedRideLines.current[rideNumber];
      if (polyline && polyline.getMap()) {
        polyline.setMap(null);
      }
    });
  };

  const showAllPlayedRideLines = () => {
    Object.keys(playedRideLines.current).forEach((rideNumber: string) => {
      const polyline = playedRideLines.current[rideNumber];
      if (polyline && !polyline.getMap() && selectedRides.includes(Number(rideNumber))) {
        polyline.setMap(map);
      }
    });
  };

  /**
   * Set ride line opacity for specified ride
   * @param {number} rideNumber
   * @param {number} opacity
   * @returns {void}
   */
  const setRideLineOpacity = (rideNumber: number, opacity: number): void => {
    if (rideLines.current && rideLines.current[rideNumber]) {
      const polyline = rideLines.current[String(rideNumber)];
      if (polyline && polyline.strokeOpacity) {
        polyline.setOptions({ strokeOpacity: opacity });
      }
    }
  };

  /**
   * Set ride line zIndex for specified ride
   * @param {number} rideNumber
   * @param {number} zIndex
   * @returns {void}
   */
  const setRideLineZIndex = (rideNumber: number, zIndex: number): void => {
    if (rideLines.current && rideLines.current[rideNumber]) {
      const polyline: google.maps.Polyline = rideLines.current[String(rideNumber)];
      if (polyline) {
        polyline.setOptions({ zIndex });
      }
    }
  };

  /**
   * Set all ride lines zIndex
   * @param {number} zIndex
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllRideLinesZIndex = (zIndex: number, exceptRideNumber?: number): void => {
    if (rideLines.current) {
      Object.keys(rideLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polyline: google.maps.Polyline = rideLines.current[rideNumber];
        if (polyline) {
          polyline.setOptions({ zIndex });
        }
      });
    }
  };

  /**
   * Set ride line color for specified ride
   * @param {number} rideNumber
   * @param {string} color
   * @returns {void}
   */
  const setRideLineColor = (rideNumber: number, color: string): void => {
    if (rideLines.current && rideLines.current[rideNumber]) {
      const polyline: google.maps.Polyline = rideLines.current[String(rideNumber)];
      if (polyline) {
        polyline.setOptions({ strokeColor: color });
      }
    }
  };

  /**
   * Set played ride line zIndex for specified ride
   * @param {number} rideNumber
   * @param {number} zIndex
   * @returns {void}
   */
  const setPlayedRideLineZIndex = (rideNumber: number, zIndex: number): void => {
    if (playedRideLines.current && playedRideLines.current[rideNumber]) {
      const polyline: google.maps.Polyline = playedRideLines.current[String(rideNumber)];
      if (polyline) {
        polyline.setOptions({ zIndex });
      }
    }
  };

  const setPlayedRideLineColor = (color: string): void => {
    if (playedRideLines.current) {
      Object.keys(playedRideLines.current).forEach((rideNumber: string) => {
        const polyline: google.maps.Polyline = playedRideLines.current[rideNumber];
        if (polyline) {
          polyline.setOptions({ strokeColor: color });
        }
      });
    }
  };

  /**
   * Reset played ride line
   * @param {number} rideNumber
   * @returns {void}
   */
  const resetPlayedRideLine = (rideNumber: number): void => {
    if (playedRideLines.current && playedRideLines.current[rideNumber]) {
      const polyline: google.maps.Polyline = playedRideLines.current[String(rideNumber)];
      if (polyline) {
        polyline.setPath([]);
      }
    }
  };

  /**
   * Set all played ride lines
   * @returns {void}
   */
  const resetAllPlayedRideLines = (): void => {
    if (playedRideLines.current) {
      Object.keys(playedRideLines.current).forEach((rideNumber: string) => {
        const polyline: google.maps.Polyline = playedRideLines.current[rideNumber];
        if (polyline) {
          polyline.setPath([]);
        }
      });
    }
  };

  /**
   * Set all ride lines color
   * @param {string} color
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllRideLinesColor = (color: string, exceptRideNumber?: number): void => {
    if (rideLines.current) {
      Object.keys(rideLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polyline: google.maps.Polyline = rideLines.current[rideNumber];
        if (polyline) {
          polyline.setOptions({ strokeColor: color });
        }
      });
    }
  };

  /**
   * Show all ride lines
   * @returns {void}
   */
  const showAllRideLines = (): void => {
    Object.keys(rideLines.current).forEach((rideNumber: string) => {
      const polyline: google.maps.Polyline = rideLines.current[rideNumber];
      if (!polyline.getMap() && selectedRides.includes(Number(rideNumber))) {
        polyline.setMap(map);
      }
    });
  };

  /**
   *  Set all played ride lines zindexes
   * @param {number} zIndex
   * @param {number} exceptRideNumber
   */
  const setAllPlayedRideLinesZindex = (zIndex: number, exceptRideNumber?: number): void => {
    if (playedRideLines.current) {
      Object.keys(playedRideLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polyline: google.maps.Polyline = rideLines.current[rideNumber];
        if (polyline) {
          polyline.setOptions({ zIndex });
        }
      });
    }
  };

  /**
   * Hide all ride lines
   * @returns {void}
   */
  const hideAllRideLines = (): void => {
    Object.keys(rideLines.current).forEach((rideNumber: string) => {
      const polyline: google.maps.Polyline = rideLines.current[rideNumber];
      if (polyline.getMap()) {
        polyline.setMap(null);
      }
    });
  };

  /**
   * Remove played lines
   * @returns {void}
   */
  const removePlayedLines = (): void => {
    Object.keys(playedRideLines.current).map((rideNumber: string) => {
      playedRideLines.current[Number(rideNumber)].setMap(null);
    });
    playedRideLines.current = createActiveRideLines(map, rides, getRideLineColorByLayer());
  };

  const removeAllRideLines = (): void => {
    hideAllRideLines();
    rideLines.current = {};
  };

  // Handle show/hide rideLines and played ridelines by selected rides
  React.useEffect(() => {
    Object.keys(rideLines.current).forEach((rideNumber: string) => {
      if (
        !selectedRides.includes(Number(rideNumber)) &&
        rideLines.current[Number(rideNumber)].getMap()
      ) {
        rideLines.current[Number(rideNumber)].setMap(null);
      }
      if (
        selectedRides.includes(Number(rideNumber)) &&
        !rideLines.current[Number(rideNumber)].getMap() &&
        activeLayer === IS_RIDE_LAYER
      ) {
        rideLines.current[Number(rideNumber)].setMap(map);
      }
    });
    Object.keys(playedRideLines.current).forEach((rideNumber: string) => {
      const playedRideLine = playedRideLines.current[rideNumber];
      if (!selectedRides.includes(Number(rideNumber)) && playedRideLine.getMap()) {
        playedRideLine.setMap(null);
      }
      if (selectedRides.includes(Number(rideNumber)) && !playedRideLine.getMap()) {
        playedRideLine.setMap(map);
      }
    });
  }, [activeLayer, map, selectedRides]);

  // On mount effect
  React.useEffect(() => {
    rideLines.current = createRideLines(map, rides);

    // Remove ride lines from map
    return () => {
      hideAllRideLines();
      rideLines.current = {};
      removePlayedLines();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If actual position is present, update rideline
  React.useEffect(() => {
    if (actualRidePosition && activeRide) {
      addNewRideLinePositionForRide(rideLines.current, activeRide, actualRidePosition);
    }
  }, [actualRidePosition, activeRide]);

  const data: IUseRideLine = {
    rideLines: rideLines.current,
    setRideLinesSize,
    showAllRideLines,
    hideAllRideLines,
    playedRideLines: playedRideLines.current,
    setRideLineOpacity,
    setRideLineColor,
    setAllRideLinesColor,
    showAllPlayedRideLines,
    hideAllPlayedRideLines,
    removePlayedLines,
    setRideLineZIndex,
    setAllRideLinesZIndex,
    setAllPlayedRideLinesZindex,
    setPlayedRideLineZIndex,
    resetPlayedRideLine,
    resetAllPlayedRideLines,
    setPlayedRideLineColor,
    removeAllRideLines,
  };

  return data;
};

export default useRideLine;
